import { memo, useEffect, useRef, useState } from "react";

import { Box, Button } from "@chakra-ui/react";
import { useGetIndividualVaultSavings } from "api/individual-savings/individual-savings-api";
import { IndividualVaultSavingsStatusEnum } from "api/individual-savings/types";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import {
  AVAILABLE_EXPORT_OPTIONS,
  ExportDataEnum,
  useExportData,
} from "hooks/useExportData";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { ResetFilterEnum } from "hooks/useTableActions";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { useLocation, useSearchParams } from "react-router-dom";
import { haveQueryParamsChanged } from "utils";
import { NO_NEW_FILTER_MESSAGE, ZERO } from "utils/constants";

import { customWarningToast } from "ui/custom-toast";
import { Export } from "ui/export";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import { DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS } from "../../consts";
import { ISavingsTableHeaderCTAsProps } from "../types";

const filterOptions: TFilterOption[] = [
  {
    type: "checkbox",
    title: "Status",
    key: "status",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Active",
        value: "active",
      },
      {
        label: "Completed",
        value: "completed",
      },
      {
        label: "Paused",
        value: "paused",
      },
      {
        label: "Stopped",
        value: "stopped",
      },
    ],
  },
  {
    type: "select",
    title: "Duration",
    key: "lockDuration",
    placeholder: "Choose a duration",
    options: [
      {
        label: "One Month",
        value: "30",
      },
      {
        label: "Two Months",
        value: "60",
      },
      {
        label: "Three Months",
        value: "90",
      },
      {
        label: "Four Months",
        value: "120",
      },
      {
        label: "Five Months",
        value: "150",
      },
      {
        label: "Six Months",
        value: "180",
      },
      {
        label: "Seven Months",
        value: "210",
      },
      {
        label: "Eight Months",
        value: "240",
      },
      {
        label: "Nine Months",
        value: "270",
      },
      {
        label: "Ten Months",
        value: "300",
      },
      {
        label: "Eleven Months",
        value: "330",
      },
      {
        label: "One Year",
        value: "360",
      },
    ],
  },
  {
    type: "date-range",
    title: "Custom Date Range",
    key: "dateRange",
  },
];

const ALL_INDIVIDUAL_VAULT_SAVINGS_STATUSES: string[] = [
  "all",
  IndividualVaultSavingsStatusEnum.ACTIVE,
  IndividualVaultSavingsStatusEnum.COMPLETED,
  "paused",
  "stopped",
];

export const TableHeaderCTAs = memo((props: ISavingsTableHeaderCTAsProps) => {
  const {
    handleRefreshTable,
    handleSearchQueryChange,
    isRefreshing,
    searchQuery,
  } = props;
  const updateQueryParams = useUpdateQueryParams();
  const { isLoading: isLoadingVaultSavings, isRefetching } =
    useGetIndividualVaultSavings();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const lockDuration = searchParams.get("lockDuration");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const { isFilterError, setIsFilterError, filterRetryCountParams } =
    useFilterQueryParams({
      apiFilterRoute: "/savings/vault/personal",
    });
  const [filterValues, setFilterValues] = useState<TFilterValues>({
    status: status ? status.split(",") : [],
    lockDuration: lockDuration || "",
    dateRange: {
      startDate,
      endDate,
    },
  });

  const filterRef = useRef<IFilterRef | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const {
    isExporting,
    handleExport,
    handleResetExport,
    handleExportTypeChange,
    handleDateRangeChange,
    dateRange,
    exportType,
    dropDownRef,
  } = useExportData({
    type: ExportDataEnum.PERSONAL_VAULT_SAVINGS,
  });

  const isFilterDisabled = !(
    (filterValues?.lockDuration as string) ||
    (filterValues?.dateRange as IDateRangeValues)?.startDate ||
    (filterValues?.status as string[])?.length > ZERO
  );

  const handleResetFilter = () => {
    setFilterValues({
      status: [],
      lockDuration: "",
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });
  };

  const handleFilterValueChange = (key: string, value: TFilterValue) => {
    let newValue = value;
    if (key === "status") {
      const vaultSavingStatus = value as string[];
      const currentVaultSavingsStatus = filterValues?.status as string[];
      if (
        vaultSavingStatus.includes("all") &&
        currentVaultSavingsStatus.length <
          ALL_INDIVIDUAL_VAULT_SAVINGS_STATUSES.length
      ) {
        newValue = ALL_INDIVIDUAL_VAULT_SAVINGS_STATUSES;
      } else {
        newValue = vaultSavingStatus.filter((trxStatus) => trxStatus !== "all");
      }
    }
    setFilterValues((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const handleApplyFilter = async () => {
    const newQueryParams: Record<string, string | null> = {};
    const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
    const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
    const status = filterValues.status;
    const lockDuration = filterValues.lockDuration;

    if (startDate) {
      newQueryParams["startDate"] = startDate;
    } else {
      newQueryParams["startDate"] = null;
    }

    if (endDate) {
      newQueryParams["endDate"] = endDate;
    } else {
      newQueryParams["endDate"] = null;
    }

    if (Array.isArray(status) && status.length > ZERO) {
      newQueryParams["status"] = status.join(",");
    } else {
      newQueryParams["status"] = null;
    }

    if (lockDuration) {
      newQueryParams["lockDuration"] = lockDuration as string;
    } else {
      newQueryParams["lockDuration"] = null;
    }

    newQueryParams["page"] =
      DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.page.toString();
    newQueryParams["limit"] =
      DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.limit.toString();
    setIsFilterError(false);
    updateQueryParams({
      ...newQueryParams,
      ...filterRetryCountParams,
      resetFilter: ResetFilterEnum.TABLE,
      isDateFilter: null,
    });

    if (
      haveQueryParamsChanged({
        currentSearch: location.search,
        newParams: newQueryParams,
      }) ||
      isFilterError
    ) {
      setIsFiltering(true);
    } else {
      customWarningToast({
        message: NO_NEW_FILTER_MESSAGE,
      });
    }
  };

  useEffect(() => {
    if (!isLoadingVaultSavings && filterRef?.current?.isFilterOpen) {
      setIsFiltering(false);
      filterRef?.current?.handleToggleFilter();
    }
  }, [isLoadingVaultSavings, filterRef]);

  useEffect(() => {
    if (!isLoadingVaultSavings && isRefreshing) {
      handleResetFilter();
    }
  }, [isLoadingVaultSavings]);

  return (
    <Box className="flex px-3 items-center justify-between h-[85px] gap-4">
      <Box className="flex items-center gap-4">
        <TableSearchInput
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
        />
        <Filter
          options={filterOptions}
          values={filterValues}
          onChange={handleFilterValueChange}
          onApplyFilter={handleApplyFilter}
          filterBtnCss={tableHeaderCtaCss}
          isLoading={isFiltering}
          ref={filterRef}
          isApplyBtnDisabled={isFilterDisabled}
          handleResetFilter={handleResetFilter}
        />
        <Export
          isLoading={isExporting}
          handleExport={handleExport}
          dateRange={dateRange}
          exportType={exportType}
          handleExportTypeChange={handleExportTypeChange}
          handleDateRangeChange={handleDateRangeChange}
          dropDownRef={dropDownRef}
          availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
          handleResetExport={handleResetExport}
        />
        <Button
          css={tableHeaderCtaCss}
          onClick={
            !isRefreshing || !isRefetching ? handleRefreshTable : undefined
          }
          disabled={isRefreshing || isRefetching}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing || isRefetching ? "animate-spin" : ""}`}
          />
          <p className="text-sm text-[#374151] font-semibold">Refresh</p>
        </Button>
      </Box>
    </Box>
  );
});
